import { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getPublicBusinessCardDetails } from '../api';
import logo from '../../dashboard/assets/logo.png';

import { BuildingIcon, ShareIcon, CopyIcon, CheckIcon } from '@/components';
import { NotFound, ServerError } from '@/common/components';
import { env } from '@/common/config';
import { classifySocialUrl } from '@/common/utils/socialLink';
import ProfileBg from '../../dashboard/assets/Profile_bg.png';
import BusinesscardBg from '../../dashboard/assets/Businesscard.png';
import AddressIconImg from '../../dashboard/assets/Address.png';
import EmailIconImg from '../../dashboard/assets/Email.png';
import FacebookIconImg from '../../dashboard/assets/Facebook_Profile.png';
import InstagramIconImg from '../../dashboard/assets/Instagram_Profile.png';
import OwnerIconImg from '../../dashboard/assets/Owner.png';
import ThreadsIconImg from '../../dashboard/assets/Threads_Profile.png';
import WhatsAppIconImg from '../../dashboard/assets/WhatsApp_Profile.png';

/* ─── Info field — Light Theme ────────────────────────────────────── */
function InfoField({ icon, label, children }) {
  return (
    <div className="flex items-start gap-3 min-w-0 w-full">
      <div className="shrink-0 w-[42px] h-[42px] flex items-center justify-center">
        {icon}
      </div>
      <div className="flex flex-col min-w-0 justify-center min-h-[40px]">
        <span className="text-body-sm font-medium text-text-muted mb-0.5">
          {label}
        </span>
        <div className="text-text-primary text-body leading-snug break-words">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ─── Quick action card — Light Theme ─────────────────────────────── */
function QuickActionCard({
  as: Tag = 'a',
  href,
  onClick,
  iconBgClass = '',
  icon,
  title,
  subtitle,
}) {
  const isWhatsApp = title === 'WhatsApp';
  const shared = {
    className: `flex items-center gap-3 rounded-xl border transition-all duration-300 cursor-pointer p-3 w-full focus:outline-none group ${
      isWhatsApp
        ? 'bg-surface-mint border-primary/20 hover:bg-surface-mint-deep'
        : 'bg-white border-border-default hover:border-primary'
    }`,
    'aria-label': title,
  };

  const content = (
    <>
      <div
        className={`flex items-center justify-center shrink-0 w-[42px] h-[42px] overflow-hidden rounded-[10px] ${iconBgClass}`}
      >
        {icon}
      </div>
      <div className="flex flex-col flex-1 min-w-0 text-left pl-1">
        <span className="text-text-primary text-body leading-tight truncate">
          {title}
        </span>
        <span className="text-text-secondary text-body-sm leading-tight mt-0.5 truncate">
          {subtitle}
        </span>
      </div>
      <svg
        className={`w-4 h-4 shrink-0 ${isWhatsApp ? 'text-primary' : 'text-text-muted group-hover:text-primary'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      </svg>
    </>
  );

  if (Tag === 'button') {
    return (
      <button onClick={onClick} {...shared} type="button">
        {content}
      </button>
    );
  }

  return (
    <a
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      {...shared}
    >
      {content}
    </a>
  );
}

/* ══════════════════════════════════════════════════════════════════
   PAGE COMPONENT
══════════════════════════════════════════════════════════════════ */
export function PublicBusinessCardPage() {
  const { shortId } = useParams();
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['publicBusinessCard', shortId],
    queryFn: () => getPublicBusinessCardDetails(shortId),
    retry: false, // Don't retry on 404/403
  });

  /* ── Loading ───────────────────────────────────────────────────── */
  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-mint-soft font-satoshi flex items-center justify-center p-4">
        <div className="animate-pulse flex flex-col gap-4 items-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-text-muted font-medium">Loading Card...</p>
        </div>
      </div>
    );
  }

  /* ── Error ─────────────────────────────────────────────────────── */
  if (error) {
    const isAuthenticated = !!localStorage.getItem('access_token');

    if (!error?.response) {
      if (isAuthenticated) return <Navigate to="/server-error" replace />;
      return <ServerError onRetry={() => window.location.reload()} />;
    }

    const errorStatus = error.response.status;
    const errorDetail = error.response.data?.detail?.toLowerCase() || '';

    if (
      errorStatus === 404 ||
      errorStatus === 403 ||
      errorDetail.includes('not found') ||
      errorDetail.includes('business_card_not_found') ||
      errorDetail.includes('inactive') ||
      errorDetail.includes('business_card_inactive')
    ) {
      if (isAuthenticated) {
        return (
          <Navigate to="/not-found" replace state={{ unavailable: 'card' }} />
        );
      }
      return <NotFound variant="card" />;
    }

    if (errorStatus >= 500) {
      if (isAuthenticated) return <Navigate to="/server-error" replace />;
      return <ServerError onRetry={() => window.location.reload()} />;
    }

    if (isAuthenticated) return <Navigate to="/server-error" replace />;
    return <ServerError onRetry={() => window.location.reload()} />;
  }

  if (!data) return null;

  /* ── Derived values ────────────────────────────────────────────── */
  const address = data.address || {};
  const formattedAddress = [
    address.line1,
    address.city,
    address.state,
    address.postal_code,
    address.country,
  ]
    .filter((part) => part && part.trim() !== '')
    .join(', ');

  const whatsappHref = classifySocialUrl('whatsapp', data.whatsapp_link).href;
  const instagramHref = classifySocialUrl(
    'instagram',
    data.instagram_link,
  ).href;
  const facebookHref = classifySocialUrl('facebook', data.facebook_link).href;
  const threadsHref = classifySocialUrl('threads', data.threads_link).href;

  const handleWhatsAppClick = () => {
    if (whatsappHref) {
      window.open(whatsappHref, '_blank', 'noopener,noreferrer');
    }
  };

  const metaTitle = `${data.company_name} | Digital Business Card`;
  let metaDescription = `View ${data.company_name}'s digital business card.`;
  if (data.company_industry) {
    metaDescription += ` ${data.company_industry}`;
    if (!data.company_industry.toLowerCase().includes('company')) {
      metaDescription += ` company`;
    }
  }
  if (data.owner_name) {
    metaDescription += ` managed by ${data.owner_name}.`;
  }

  const handleShareCard = () => {
    if (navigator.share) {
      navigator
        .share({
          title: metaTitle,
          text: metaDescription,
          url: window.location.href,
        })
        .catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const profileImageSrc = data.profile_image_url
    ? data.profile_image_url.startsWith('http')
      ? data.profile_image_url
      : `${env.photoBaseUrl}${data.profile_image_url}`
    : null;

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content={
            profileImageSrc ||
            'https://flewa.com/images/default-share-preview.jpg'
          }
        />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="Flewa" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta
          name="twitter:image"
          content={
            profileImageSrc ||
            'https://flewa.com/images/default-share-preview.jpg'
          }
        />
      </Helmet>

      {/* Main Page Canvas with Light Green Dotted Background */}
      <div
        className="min-h-screen font-satoshi flex flex-col items-center justify-center p-3 sm:p-4 md:p-6 lg:p-6 xl:p-10 w-full overflow-x-hidden box-border"
        style={{
          backgroundImage: `url(${ProfileBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="w-full max-w-[1440px] mx-auto flex flex-col gap-4 box-border">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-stretch justify-center w-full box-border">
            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━LEFT CARD━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            <div className="flex-none lg:flex-1 lg:min-w-[580px] xl:min-w-0 lg:max-w-[900px] w-full lg:min-h-[684px] relative bg-primary rounded-[24px] shadow-sm flex flex-col overflow-hidden box-border">
              {/* ── MOBILE/TABLET GREEN HEADER ── */}
              <div className="flex lg:hidden relative w-full h-[150px] md:h-[200px] bg-primary shrink-0 overflow-hidden">
                {/* Large semi-transparent circular arc top-left */}
                <div className="absolute -top-16 -left-16 w-48 h-48 md:w-64 md:h-64 rounded-full border-[24px] md:border-[32px] border-white/10 pointer-events-none"></div>

                {/* Soft curved shape at the bottom */}
                <svg
                  className="absolute bottom-0 left-0 w-full pointer-events-none"
                  viewBox="0 0 1440 120"
                  preserveAspectRatio="none"
                  style={{ height: '45%' }}
                >
                  <path
                    fill="rgba(255,255,255,0.06)"
                    d="M0,60 C320,120 420,0 720,40 C1020,80 1120,0 1440,60 L1440,120 L0,120 Z"
                  ></path>
                  <path
                    fill="rgba(255,255,255,0.04)"
                    d="M0,40 C400,100 600,-20 900,20 C1200,60 1300,10 1440,40 L1440,120 L0,120 Z"
                  ></path>
                </svg>

                {/* Subtle decorative elements */}
                <div className="absolute top-1/4 right-[25%] w-16 h-16 rounded-full bg-white/10 blur-xl pointer-events-none"></div>
                <div className="absolute bottom-10 right-12 w-6 h-6 md:w-8 md:h-8 rounded-full border-2 md:border-[3px] border-white/10 pointer-events-none"></div>
              </div>

              {/* ── DESKTOP GREEN SIDEBAR ── */}
              <div
                className="hidden lg:flex absolute top-0 left-0 bottom-0 w-[125px] flex-col justify-center items-center"
                style={{
                  backgroundImage: `url(${BusinesscardBg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div className="-rotate-90 whitespace-nowrap text-label font-medium tracking-[0.15em] text-white z-10">
                  Contact &bull; Share &bull; Grow
                </div>
              </div>

              {/* ── MAIN CONTENT AREA ── */}
              <div className="flex-auto p-5 md:p-8 lg:py-8 lg:px-6 xl:px-8 w-full lg:w-[calc(100%-120px)] lg:ml-auto bg-white rounded-t-[24px] lg:rounded-tr-[24px] lg:rounded-br-[24px] lg:rounded-l-none flex flex-col relative z-10 -mt-[30px] md:-mt-[70px] lg:mt-0 box-border">
                {/* Dots Icon Top Right */}
                <div className="absolute top-6 right-5 sm:top-7 sm:right-6 md:top-6 md:right-6 lg:top-8 lg:right-8 grid grid-cols-4 gap-1">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-primary/80"
                    ></div>
                  ))}
                </div>

                {/* Profile Header */}
                <div className="flex flex-col lg:flex-row items-center text-center lg:text-left gap-3 lg:gap-4 -mt-[64px] lg:mt-2 lg:-ml-[52px] relative z-10">
                  <div className="w-[104px] h-[104px] md:w-[120px] md:h-[120px] lg:w-[104px] lg:h-[104px] shrink-0 rounded-full border-[6px] border-white shadow-[0_4px_16px_rgba(0,0,0,0.1)] bg-white overflow-hidden flex items-center justify-center">
                    {profileImageSrc ? (
                      <img
                        src={profileImageSrc}
                        alt={`${data.company_name} logo`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-full h-full items-center justify-center ${profileImageSrc ? 'hidden' : 'flex'}`}
                    >
                      <BuildingIcon className="w-8 h-8 text-primary" />
                    </div>
                  </div>

                  <div className="flex flex-col items-center lg:items-start justify-center self-center my-auto">
                    <h1 className="text-h3 md:text-h2 font-bold text-text-primary leading-tight break-words">
                      {data.company_name}
                    </h1>
                  </div>
                </div>

                {/* Business Info Grid */}
                <div className="border border-border-default rounded-2xl p-4 md:p-6 lg:p-5 mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-2 gap-y-5 md:gap-y-6 gap-x-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
                  {data.owner_name && (
                    <InfoField
                      label="Owner Name"
                      icon={
                        <img
                          src={OwnerIconImg}
                          alt="Owner"
                          className="w-full h-full object-cover rounded-xl"
                        />
                      }
                    >
                      {data.owner_name}
                    </InfoField>
                  )}

                  {data.owner_email && (
                    <InfoField
                      label="Email"
                      icon={
                        <img
                          src={EmailIconImg}
                          alt="Email"
                          className="w-full h-full object-cover rounded-xl"
                        />
                      }
                    >
                      <a
                        href={`mailto:${data.owner_email}`}
                        className="text-text-primary hover:text-primary transition-colors break-all"
                      >
                        {data.owner_email}
                      </a>
                    </InfoField>
                  )}

                  {formattedAddress && (
                    <div className="md:col-span-2">
                      <InfoField
                        label="Address"
                        icon={
                          <img
                            src={AddressIconImg}
                            alt="Address"
                            className="w-full h-full object-cover rounded-xl"
                          />
                        }
                      >
                        <span className="leading-relaxed block pr-4">
                          {formattedAddress}
                        </span>
                      </InfoField>
                    </div>
                  )}
                </div>

                {/* Quick Actions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5 lg:mt-6">
                  {data.owner_email && (
                    <QuickActionCard
                      as="a"
                      href={`mailto:${data.owner_email}`}
                      icon={
                        <img
                          src={EmailIconImg}
                          alt="Email"
                          className="w-full h-full object-cover"
                        />
                      }
                      iconBgClass="bg-transparent"
                      title="Send Email"
                      subtitle={data.owner_email}
                    />
                  )}

                  {formattedAddress && (
                    <QuickActionCard
                      as="a"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress)}`}
                      icon={
                        <img
                          src={AddressIconImg}
                          alt="Address"
                          className="w-full h-full object-cover"
                        />
                      }
                      iconBgClass="bg-transparent"
                      title="View Location"
                      subtitle="Open In Maps"
                    />
                  )}

                  {whatsappHref && (
                    <QuickActionCard
                      as="button"
                      onClick={handleWhatsAppClick}
                      icon={
                        <img
                          src={WhatsAppIconImg}
                          alt="WhatsApp"
                          className="w-full h-full object-cover"
                        />
                      }
                      iconBgClass="bg-transparent"
                      title="WhatsApp"
                      subtitle="Open Chat"
                    />
                  )}

                  {instagramHref && (
                    <QuickActionCard
                      as="a"
                      href={instagramHref}
                      icon={
                        <img
                          src={InstagramIconImg}
                          alt="Instagram"
                          className="w-full h-full object-cover"
                        />
                      }
                      iconBgClass="bg-transparent"
                      title="Instagram"
                      subtitle="Open Profile"
                    />
                  )}

                  {facebookHref && (
                    <QuickActionCard
                      as="a"
                      href={facebookHref}
                      icon={
                        <img
                          src={FacebookIconImg}
                          alt="Facebook"
                          className="w-full h-full object-cover"
                        />
                      }
                      iconBgClass="bg-transparent"
                      title="Facebook"
                      subtitle="Open Profile"
                    />
                  )}

                  {threadsHref && (
                    <QuickActionCard
                      as="a"
                      href={threadsHref}
                      icon={
                        <img
                          src={ThreadsIconImg}
                          alt="Threads"
                          className="w-full h-full object-cover"
                        />
                      }
                      iconBgClass="bg-transparent"
                      title="Thread"
                      subtitle="Open Profile"
                    />
                  )}

                  {data.linkedin_link && (
                    <QuickActionCard
                      as="a"
                      href={data.linkedin_link}
                      icon={
                        <svg
                          className="w-5 h-5 text-white"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      }
                      iconBgClass="bg-[#0A66C2]"
                      title="LinkedIn"
                      subtitle="Open Profile"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                RIGHT CARD
            ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            <div className="flex-none lg:flex-1 lg:min-w-[320px] xl:min-w-0 lg:max-w-[500px] w-full lg:min-h-[684px] bg-white rounded-[24px] shadow-sm p-6 md:p-8 lg:p-6 xl:p-[28px] flex flex-col justify-between items-center text-center box-border">
              <div className="w-full flex flex-col text-left">
                <h2 className="font-bold text-h5 text-text-primary mb-1">
                  Share This Business Card
                </h2>
                <p className="text-text-secondary text-body-sm leading-relaxed mb-6">
                  This public link can be shared anywhere. Scan the QR code to
                  open on any device.
                </p>
              </div>

              <div className="w-full flex justify-center mt-6 mb-6 lg:mt-auto lg:mb-auto">
                <div className="p-4 md:p-5 bg-white rounded-[28px] border-[2.5px] border-primary inline-flex relative max-w-full aspect-square w-[220px] xl:w-[260px] shadow-[0_4px_24px_rgba(80,171,61,0.15)] box-border">
                  <div className="absolute inset-1.5 border border-primary/20 rounded-[20px] pointer-events-none" />
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(window.location.href)}&margin=0`}
                    alt="QR Code"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              <div className="w-full flex items-center gap-2 mt-8">
                <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-surface-mint-soft rounded-xl border border-border-default overflow-hidden shadow-inner">
                  <span className="text-text-secondary text-body-sm truncate font-medium flex-1 text-left">
                    {window.location.href}
                  </span>
                  <button
                    onClick={handleCopyLink}
                    className={`ml-auto shrink-0 p-1.5 rounded-md transition-colors ${
                      copied
                        ? 'text-primary'
                        : 'text-text-muted hover:text-primary'
                    }`}
                    title={copied ? 'Copied!' : 'Copy Link'}
                  >
                    {copied ? (
                      <CheckIcon className="w-[16px] h-[16px]" />
                    ) : (
                      <CopyIcon className="w-[16px] h-[16px]" />
                    )}
                  </button>
                </div>

                <button
                  onClick={handleShareCard}
                  className="shrink-0 flex items-center justify-center bg-primary hover:bg-primary-hover text-white rounded-xl w-[42px] h-[42px] shadow-md transition-all active:scale-95"
                  title="Share Card"
                >
                  <ShareIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━FOOTER━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          <div className="w-full flex justify-center">
            <a
              href="https://aexon.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full max-w-[1417px] bg-white py-3 sm:py-[14px] px-4 sm:px-[28px] rounded-[12px] shadow-sm flex justify-center items-center gap-2.5 sm:gap-[14px] hover:bg-surface-mint-soft transition-colors"
            >
              <span className="text-text-muted text-sm sm:text-base md:text-lg font-medium tracking-wide whitespace-nowrap">
                Powered By
              </span>
              <img
                src={logo}
                alt="Aexon"
                className="h-8 sm:h-10 md:h-[48px] lg:h-[54px] w-auto object-contain shrink-0"
              />
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
